import React, { useCallback, useMemo } from 'react';
import { Spinner } from '@heroui/react';
import { UniversalTable, IdView, Typography } from '../../../../components';
import { EmptyContentIc } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import { PaginationInfo, TableColumn } from '../../../../types/account';
import { RefShareOrder, OrderStatus } from '../../../../api/services';
import OwnerCell from './OwnerCell';
import CampaignCell from './CampaignCell';
import CampaignStatisticsCell from './CampaignStatisticsCell';
import PlatformIncomeCell from './PlatformIncomeCell';
import CampaignStatusCell from './CampaignStatusCell';
import MarketerCell from './MarketerCell';
import CampaignActionsCell from './CampaignActionsCell';

type RevshareCampaignsTableProps = {
  orders: RefShareOrder[];
  pagination: PaginationInfo;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onStatusChange?: (orderId: string, status: OrderStatus) => void;
  totalPlatformIncome?: number;
  sortBy?: string;
  onSort?: (sortKey: string) => void;
};

export const RevshareCampaignsTable: React.FC<RevshareCampaignsTableProps> = ({
  orders,
  pagination,
  isLoading,
  onPageChange,
  onPageSizeChange,
  onStatusChange,
  totalPlatformIncome = 0,
  sortBy,
  onSort,
}) => {
  const { t } = useTranslations();

  const columns: TableColumn[] = useMemo(() => [
    { key: 'id', name: 'ID кампании' },
    { key: 'owner', name: 'Владелец' },
    { key: 'campaign', name: 'Кампания' },
    { key: 'statistics', name: 'Статистика' },
    { key: 'platformIncome', name: `Доход $${totalPlatformIncome.toFixed(2)} (5%)` },
    { key: 'status', name: 'Статус', sortable: true, sortKey: 'created_at' },
    { key: 'marketer', name: 'Маркетолог' },
    { key: 'actions', name: 'Действия' },
  ], [totalPlatformIncome]);

  const renderCell = useCallback((order: RefShareOrder, columnKey: string) => {
    const orderId = order._id || order.id || '';

    switch (columnKey) {
      case 'id':
        return (
          <IdView textToCopy={orderId}>
            {orderId.slice(-4)}
          </IdView>
        );

      case 'owner':
        return <OwnerCell order={order} />;

      case 'campaign':
        return <CampaignCell order={order} />;

      case 'statistics':
        return <CampaignStatisticsCell order={order} />;

      case 'platformIncome':
        return <PlatformIncomeCell order={order} />;

      case 'status':
        return <CampaignStatusCell order={order} />;

      case 'marketer':
        return <MarketerCell order={order} />;

      case 'actions':
        return <CampaignActionsCell order={order} onStatusChange={onStatusChange} />;

      default:
        return null;
    }
  }, [onStatusChange]);

  const emptyContent = isLoading ? (
    <div className="flex flex-col items-center justify-center py-[48px] gap-[12px]">
      <Spinner color="primary" />
      <Typography variant="body2" className="text-[#71717A]">
        Загрузка...
      </Typography>
    </div>
  ) : (
    <div className="flex flex-col items-center justify-center py-[48px] gap-[12px]">
      <EmptyContentIc />
      <Typography variant="body1" className="font-semibold">
        Кампании не найдены
      </Typography>
      <Typography variant="body3" className="text-[#71717A]">
        Попробуйте изменить параметры поиска
      </Typography>
    </div>
  );

  return (
    <UniversalTable
      data={orders}
      columns={columns}
      renderCell={renderCell}
      emptyContent={emptyContent}
      pagination={pagination}
      currentPage={pagination.page}
      currentPageSize={pagination.page_size}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      isSearchEmpty={!orders.length}
      sortBy={sortBy}
      onSort={onSort}
    />
  );
};
